import { oc } from "@orpc/contract";
import { z } from "zod";
import { daysBetween } from "@erp/litellm-usage";
import type {
  AvailableUsageDimensions,
  TagNamespaceUsage,
  TagUsage,
  TagUsageRow,
  UsageDay,
  UsageDimensionRow,
  UsageMetrics,
} from "@erp/litellm-usage";

// Current-environment LiteLLM spend + budget, read from the proxy's /key/info
// for this env's virtual key. `configured` is false when LITELLM_PROXY_URL /
// LITELLM_VIRTUAL_KEY are unset (routing disabled) — the page then shows a
// "not configured" notice instead of zeros.
export const AiSpendSummarySchema = z.object({
  configured: z.boolean(),
  environment: z.string().nullable(),
  spend: z.number().nullable(),
  maxBudget: z.number().nullable(),
  budgetResetAt: z.string().nullable(),
  models: z.array(z.string()),
});

export type AiSpendSummary = z.infer<typeof AiSpendSummarySchema>;

// ── per-tag attribution ─────────────────────────────────────────────────────
//
// A SIBLING procedure rather than an extension of `get`, deliberately:
//
//  - Different semantics. `get` reads /key/info, whose `spend` is a running counter for the
//    current BUDGET window. This reads a caller-chosen date range. Merging them invites the
//    worst bug available here — someone subtracting a budget-window number from a
//    range-scoped one.
//  - Different failure domains. `get` is one cheap call; this is a fan-out that can be
//    scope-rejected. Keeping them apart means the budget cards still render when tag
//    attribution is unavailable.
//  - Backward compatibility. `useAiSpendSummary` already calls `queryOptions({})`, so adding
//    required input to `get` would break it.
//
// Types come from `@erp/litellm-usage`; these schemas `satisfies` them, so a change to the
// reader that this contract does not reflect is a compile error rather than a runtime shape
// mismatch.

export const AiUsageMetricsSchema = z.object({
  spend: z.number(),
  promptTokens: z.number(),
  completionTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheCreationTokens: z.number(),
  requests: z.number(),
  successfulRequests: z.number(),
  failedRequests: z.number(),
}) satisfies z.ZodType<UsageMetrics>;

const usageDaySchema = z.object({
  date: z.string(),
  spend: z.number(),
  requests: z.number(),
  totalTokens: z.number(),
}) satisfies z.ZodType<UsageDay>;

// Key origins — which virtual key each slice of spend was billed to — are deliberately not
// exposed to the app, so the shapes below drop them from the reader's types.
type ExposedTagUsageRow = Omit<TagUsageRow, "origins">;
type ExposedTagNamespaceUsage = Omit<TagNamespaceUsage, "rows"> & {
  rows: ExposedTagUsageRow[];
};
type ExposedTagUsage = Omit<TagUsage, "namespaces"> & {
  namespaces: ExposedTagNamespaceUsage[];
};

const tagUsageRowSchema = z.object({
  tag: z.string(),
  namespace: z.string(),
  value: z.string(),
  // Null when unresolvable — a hard-deleted row, an id from another environment's database,
  // or a name withheld from a viewer outside its org. Soft-deleted rows and self-describing
  // namespaces (skill, module, feature) DO resolve; the UI renders a truncated-id fallback
  // for the rest.
  label: z.string().nullable(),
  metrics: AiUsageMetricsSchema,
  daily: z.array(usageDaySchema),
}) satisfies z.ZodType<ExposedTagUsageRow>;

const tagNamespaceUsageSchema = z.object({
  namespace: z.string(),
  rows: z.array(tagUsageRowSchema),
  // Valid only WITHIN this namespace: a request carries at most one tag per namespace, so
  // summing within one is sound while summing across two would count it twice.
  attributed: AiUsageMetricsSchema,
  unattributedSpend: z.number().nullable(),
  // Not clamped to 1 — above 1 means aggregation lag, and hiding that behind a clamp turns a
  // legible cause into an inexplicable number.
  coverage: z.number().nullable(),
  silentTagCount: z.number(),
  truncated: z.boolean(),
}) satisfies z.ZodType<ExposedTagNamespaceUsage>;

export const AiTagUsageSchema = z.object({
  configured: z.boolean(),
  // False means "unavailable" — the UI must say so rather than render zeros, which read as
  // "nothing was spent".
  available: z.boolean(),
  startDate: z.string(),
  endDate: z.string(),
  // From /team/daily/activity, the only trustworthy denominator.
  windowTotals: AiUsageMetricsSchema,
  denominatorSource: z.enum(["team-daily-activity", "none"]),
  namespaces: z.array(tagNamespaceUsageSchema),
  ignoredTags: z.array(z.string()),
  warnings: z.array(z.string()),
}) satisfies z.ZodType<ExposedTagUsage>;

export type AiTagUsage = z.infer<typeof AiTagUsageSchema>;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Bounds the proxy fan-out. Ninety-two days covers a quarter.
 *
 * Exported because the contract-level refine below can only see the dates that were sent —
 * a single-sided input passes it, so the service re-checks this cap AFTER filling defaults.
 * The refine stays for the errors it can catch without a round trip.
 */
export const AI_TAG_USAGE_MAX_WINDOW_DAYS = 92;
const MAX_WINDOW_DAYS = AI_TAG_USAGE_MAX_WINDOW_DAYS;

export const AiTagUsageInputSchema = z
  .object({
    // UTC, because the proxy buckets its daily aggregates by UTC date.
    startDate: z.string().regex(DATE_ONLY).optional(),
    endDate: z.string().regex(DATE_ONLY).optional(),
  })
  .refine(
    (range) =>
      !range.startDate || !range.endDate || range.startDate <= range.endDate,
    { message: "startDate must not be after endDate" },
  )
  .refine(
    (range) =>
      !range.startDate ||
      !range.endDate ||
      daysBetween(range.startDate, range.endDate) <= MAX_WINDOW_DAYS,
    { message: `window must not exceed ${MAX_WINDOW_DAYS} days` },
  );

export type AiTagUsageInput = z.infer<typeof AiTagUsageInputSchema>;

const usageDimensionRowSchema = z.object({
  name: z.string(),
  metrics: AiUsageMetricsSchema,
}) satisfies z.ZodType<UsageDimensionRow>;

const availableUsageDimensionsSchema = z.object({
  models: z.boolean(),
  modelGroups: z.boolean(),
  providers: z.boolean(),
}) satisfies z.ZodType<AvailableUsageDimensions>;

export const AiUsageDimensionsSchema = z.object({
  configured: z.boolean(),
  available: z.boolean(),
  complete: z.boolean(),
  startDate: z.string(),
  endDate: z.string(),
  totals: AiUsageMetricsSchema,
  availableDimensions: availableUsageDimensionsSchema,
  models: z.array(usageDimensionRowSchema),
  modelGroups: z.array(usageDimensionRowSchema),
  providers: z.array(usageDimensionRowSchema),
});

export type AiUsageDimensions = z.infer<typeof AiUsageDimensionsSchema>;

export const AiWorkflowFamilySchema = z.object({
  workflowId: z.string(),
  label: z.string().nullable(),
  spend: z.number(),
  versionCount: z.number().int().nonnegative(),
  identityAvailable: z.boolean(),
});

export const AiWorkflowUsageSchema = z.object({
  configured: z.boolean(),
  available: z.boolean(),
  complete: z.boolean(),
  startDate: z.string(),
  endDate: z.string(),
  attributedSpend: z.number(),
  coverage: z.number().nullable(),
  families: z.array(AiWorkflowFamilySchema),
});

export type AiWorkflowUsage = z.infer<typeof AiWorkflowUsageSchema>;

export const AiWorkflowVersionsInputSchema = z.intersection(
  AiTagUsageInputSchema,
  z.object({ workflowId: z.string().uuid() }),
);

export type AiWorkflowVersionsInput = z.infer<
  typeof AiWorkflowVersionsInputSchema
>;

export const AiWorkflowVersionSchema = z.object({
  definitionId: z.string().uuid(),
  version: z.number().int().positive(),
  status: z.enum(["draft", "finalized"]),
  isPinned: z.boolean(),
  changelog: z.string().nullable(),
  metrics: AiUsageMetricsSchema,
});

export const AiUsageDetailStateSchema = z.enum([
  "complete",
  "partial",
  "empty",
  "unavailable",
  "identity_withheld",
]);

export type AiUsageDetailState = z.infer<typeof AiUsageDetailStateSchema>;

export const AiWorkflowVersionsSchema = z.object({
  configured: z.boolean(),
  state: AiUsageDetailStateSchema,
  startDate: z.string(),
  endDate: z.string(),
  workflowId: z.string().uuid(),
  totalDiscoveredVersions: z.number().int().nonnegative(),
  versions: z.array(AiWorkflowVersionSchema),
});

export type AiWorkflowVersions = z.infer<typeof AiWorkflowVersionsSchema>;

export const AiWorkflowStepsInputSchema = z.intersection(
  AiTagUsageInputSchema,
  z.object({ workflowDefinitionId: z.string().uuid() }),
);

export type AiWorkflowStepsInput = z.infer<typeof AiWorkflowStepsInputSchema>;

export const AiWorkflowStepSchema = z.object({
  workflowStepId: z.string().uuid(),
  name: z.string(),
  stepType: z.string(),
  metrics: AiUsageMetricsSchema,
});

export const AiWorkflowStepsSchema = z.object({
  configured: z.boolean(),
  state: AiUsageDetailStateSchema,
  startDate: z.string(),
  endDate: z.string(),
  workflowDefinitionId: z.string().uuid(),
  rows: z.array(AiWorkflowStepSchema),
});

export type AiWorkflowSteps = z.infer<typeof AiWorkflowStepsSchema>;

export const aiUsageContracts = {
  get: oc.output(AiSpendSummarySchema).route({
    method: "GET",
    summary: "Get AI spend and budget",
    description:
      "Returns this environment's AI model spend for the current budget window, the budget cap, when it resets and which models are allowed; flags when AI routing is not configured.",
  }),
  tags: oc.input(AiTagUsageInputSchema).output(AiTagUsageSchema).route({
    method: "GET",
    summary: "Break down AI spend by tag",
    description:
      "Returns AI spend, tokens and requests for a date range (up to 92 days, UTC), grouped by attribution tag such as module, feature or skill, with daily series and coverage.",
  }),
  dimensions: oc
    .input(AiTagUsageInputSchema)
    .output(AiUsageDimensionsSchema)
    .route({
      method: "GET",
      summary: "Break down AI spend by model",
      description:
        "Returns AI spend, tokens and request counts for a date range (up to 92 days), broken down by model, model group and provider.",
    }),
  workflows: oc
    .input(AiTagUsageInputSchema)
    .output(AiWorkflowUsageSchema)
    .route({
      method: "GET",
      summary: "Break down AI spend by workflow",
      description:
        "Returns AI spend for a date range (up to 92 days) per workflow, with version counts and how much spend could be attributed to a workflow.",
    }),
  workflowVersions: oc
    .input(AiWorkflowVersionsInputSchema)
    .output(AiWorkflowVersionsSchema)
    .route({
      method: "GET",
      summary: "Break down a workflow's AI spend by version",
      description:
        "Returns AI spend, tokens and requests for one workflow over a date range, per version, with draft or finalized status, pin flag and changelog.",
    }),
  workflowSteps: oc
    .input(AiWorkflowStepsInputSchema)
    .output(AiWorkflowStepsSchema)
    .route({
      method: "GET",
      summary: "Break down AI spend by workflow step",
      description:
        "Returns AI spend, tokens and requests for each step of one workflow version over a date range (up to 92 days).",
    }),
} as const;
